const normalizeName = (value) => String(value || '').trim().toLocaleLowerCase();

const getAppId = (query) => query?.app_id ?? query?.appId ?? query?.app?.id ?? '';

const getQueryCategory = (query) => normalizeName(query?.category || query?.type);

// Clé d’unicité de query_catalog : app_id, name, category.
const getImportKey = (query, appId = getAppId(query)) => JSON.stringify([
    String(appId), normalizeName(query.name), getQueryCategory(query),
]);

const normalizeLineEndings = (value) => String(value ?? '').replace(/\r\n?/g, '\n');

const escapeIniScalar = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value).replace(/\r?\n/g, '\\n');
};

const formatIniMultilineValue = (key, value) => {
    const [first, ...rest] = normalizeLineEndings(value).split('\n');
    return [`${key} = ${first}`, ...rest.map((line) => `    ${line}`)].join('\n');
};

const sanitizeSectionPart = (value, fallback) => String(value || fallback || 'query')
    .trim()
    .replace(/[\[\]\r\n]/g, '_')
    .replace(/\s+/g, '_');

export const buildQueryIni = (queries, { filterLabel = 'app=*, type=*', resolveAppLabel = () => '', date = new Date().toISOString() } = {}) => {
    const header = [
        '; Export nVisio queries',
        `; Date = ${date}`,
        `; Filtres = ${filterLabel}`,
        `; Total = ${queries.length}`,
        '',
    ];
    const sections = queries.map((query) => [
        `[query.${query.id ?? 'new'}.${sanitizeSectionPart(query.name, `query_${query.id}`)}]`,
        `id = ${escapeIniScalar(query.id)}`,
        `name = ${escapeIniScalar(query.name)}`,
        formatIniMultilineValue('label', query.label ?? ''),
        `app_id = ${escapeIniScalar(getAppId(query))}`,
        `app = ${escapeIniScalar(resolveAppLabel(query))}`,
        `category = ${escapeIniScalar(query.category || query.type)}`,
        formatIniMultilineValue('template', query.template || ''),
        '',
    ].join('\n'));

    return [...header, ...sections].join('\n');
};

export const parseQueryIni = (content) => {
    const lines = String(content).replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').split('\n');
    const queries = [];
    let declaredTotal = null;
    let current = null;
    let currentKey = '';

    const finish = () => {
        if (!current) return;
        if (!current.name || !current.category || !current.template.trim()) {
            throw new Error(`La section [${current.section}] doit contenir name, category et template.`);
        }
        queries.push(current);
    };

    lines.forEach((line, index) => {
        const section = line.match(/^\[([^\]]+)\]\s*$/);
        if (section) {
            if (!/^query\.[^.]+\..+$/.test(section[1])) {
                throw new Error(`Section non prise en charge à la ligne ${index + 1} : [${section[1]}].`);
            }
            finish();
            current = { section: section[1], id: '', name: '', label: '', app_id: '', app: '', category: '', template: '' };
            currentKey = '';
            return;
        }

        if (!current) {
            const total = line.match(/^;\s*Total\s*=\s*(\d+)\s*$/i);
            if (total) declaredTotal = Number(total[1]);
            if (line.trim() && !line.trimStart().startsWith(';')) {
                throw new Error(`Contenu inattendu à la ligne ${index + 1}.`);
            }
            return;
        }

        if (/^\s/.test(line) && line.trim()) {
            if (currentKey !== 'template' && currentKey !== 'label') {
                throw new Error(`Ligne de continuation inattendue à la ligne ${index + 1}.`);
            }
            const value = line.startsWith('    ') ? line.slice(4) : line.startsWith('\t') ? line.slice(1) : line;
            current[currentKey] += `\n${value}`;
            return;
        }

        if (!line.trim()) {
            if ((currentKey === 'template' || currentKey === 'label') && index + 1 < lines.length && !/^\[[^\]]+\]\s*$/.test(lines[index + 1])) {
                current[currentKey] += '\n';
            }
            return;
        }

        if (line.trimStart().startsWith(';')) return;

        const field = line.match(/^([A-Za-z0-9_]+)\s*=\s*(.*)$/);
        if (!field || !(field[1] in current) || field[1] === 'section') {
            throw new Error(`Champ INI non reconnu à la ligne ${index + 1}.`);
        }
        currentKey = field[1];
        current[currentKey] = field[2];
    });

    finish();
    if (queries.length === 0) throw new Error('Le fichier ne contient aucune query au format exporté.');
    if (declaredTotal !== null && declaredTotal !== queries.length) {
        throw new Error(`Le fichier annonce ${declaredTotal} queries mais en contient ${queries.length}.`);
    }
    return queries;
};

export const buildQueryImportPlan = (queries, apps, existingQueries, selectedAppId = '') => {
    const normalizedApps = apps.map((app) => ({
        ...app,
        names: [app.label, app.name, app.code, app.title].filter(Boolean).map(normalizeName),
    }));
    const rows = queries.map((query) => {
        const appName = normalizeName(query.app);
        const matchesByName = appName ? normalizedApps.filter((app) => app.names.includes(appName)) : [];
        const appId = query.app_id || selectedAppId;
        const matches = matchesByName.length > 0
            ? matchesByName
            : (!appName || appName === normalizeName(`App #${query.app_id}`))
                ? normalizedApps.filter((app) => String(app.id) === String(appId))
                : [];
        const app = matches.length === 1 ? matches[0] : null;

        if (!app) {
            return { query, action: 'error', reason: appName
                ? `Application « ${query.app} » introuvable ou ambiguë.`
                : 'Application absente ou introuvable. Sélectionnez une app dans les filtres.' };
        }

        const importKey = getImportKey(query, app.id);
        const matchingQueries = existingQueries.filter((existing) => getImportKey(existing) === importKey);
        if (matchingQueries.length > 1) {
            return { query, app, action: 'error', reason: 'Plusieurs queries existantes portent ce nom et cette catégorie dans cette app.' };
        }

        const payload = {
            name: query.name.trim(),
            label: query.label,
            app_id: app.id,
            category: query.category.trim(),
            template: query.template,
        };
        return { query, app, payload, existingId: matchingQueries[0]?.id, action: matchingQueries.length ? 'update' : 'create' };
    });

    const counts = new Map();
    rows.forEach((row) => {
        if (row.app) {
            const key = getImportKey(row.query, row.app.id);
            counts.set(key, (counts.get(key) || 0) + 1);
        }
    });
    return rows.map((row) => {
        if (row.app && counts.get(getImportKey(row.query, row.app.id)) > 1) {
            return { ...row, action: 'error', reason: 'Ce nom et cette catégorie sont présents plusieurs fois pour la même app dans le fichier.' };
        }
        return row;
    });
};
